/**
 * AccouPro — GSTIN Verification & Company Auto-Fill Component
 * 
 * Provides:
 * 1. 15-character uppercase formatted input with live structure & Luhn Modulo 36 checksum helper
 * 2. "Verify GSTIN" action button with loading spinner
 * 3. Status badges (Active, Cancelled, Suspended, Invalid, Unverified (Format Valid), Unable to Verify)
 * 4. Confirmation Preview Modal with "Current" vs "GST Portal" diff before auto-populating
 * 5. Honest missing fields identification (NEVER displays fake placeholder strings)
 * 6. Full post-population editability and manual entry fallback
 */

import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Loader2,
  Info,
  ArrowRight,
  Sparkles,
  Building2,
  MapPin,
  Database,
  ExternalLink,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GSTVerificationResult, GSTRegistrationStatus } from "@/types/gst";
import { verifyGSTINApi } from "@/lib/api/gst";
import {
  validateGSTIN,
  validateGSTINChecksum,
  extractFromGSTIN,
} from "@/lib/onboarding/gstUtils";

export interface GSTINVerificationInputProps {
  value: string;
  onChange: (value: string) => void;
  onApplyVerification: (data: GSTVerificationResult) => void;
  currentFormValues?: {
    name?: string;
    legalName?: string;
    pan?: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    compositionScheme?: boolean;
    businessConstitution?: string;
  };
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  isEditMode?: boolean;
  className?: string;
}

export function GSTINVerificationInput({
  value,
  onChange,
  onApplyVerification,
  currentFormValues,
  label = "GSTIN",
  placeholder = "03ABWPS8184L1ZN",
  disabled = false,
  required = false,
  isEditMode = false,
  className = "",
}: GSTINVerificationInputProps) {
  const { toast } = useToast();

  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedResult, setVerifiedResult] = useState<GSTVerificationResult | null>(null);
  const [verificationSource, setVerificationSource] = useState<string>("");
  const [verificationMessage, setVerificationMessage] = useState<string>("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const cleanGstin = (value || "").trim().toUpperCase();

  // Live client-side validation & extraction
  const liveAnalysis = useMemo(() => {
    if (!cleanGstin) {
      return {
        hasInput: false,
        isComplete: false,
        formatValid: false,
        checksumValid: false,
        extracted: null,
        message: "",
      };
    }

    const isComplete = cleanGstin.length === 15;
    const formatValid = validateGSTIN(cleanGstin, false);
    const checksumCheck = isComplete ? validateGSTINChecksum(cleanGstin) : { valid: false };
    const extracted = extractFromGSTIN(cleanGstin);

    let message = "";
    if (!isComplete) {
      message = `${cleanGstin.length}/15 chars`;
      if (extracted.state) {
        message += ` • ${extracted.state.name} (${extracted.stateCode})`;
      }
    } else if (!formatValid) {
      message = "Invalid GSTIN pattern (State + PAN + Entity + Z + Checksum)";
    } else if (!checksumCheck.valid) {
      message = checksumCheck.error || "Checksum character mismatch";
    } else {
      message = "Format & Luhn Checksum Valid";
    }

    return {
      hasInput: true,
      isComplete,
      formatValid,
      checksumValid: checksumCheck.valid,
      checksumExpected: checksumCheck.expected,
      checksumActual: checksumCheck.actual,
      extracted,
      message,
    };
  }, [cleanGstin]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15);
    onChange(raw);

    // If GSTIN changed, clear previously verified result if it doesn't match
    if (verifiedResult && verifiedResult.gstin !== raw) {
      setVerifiedResult(null);
    }
  };

  const handleVerify = async () => {
    if (cleanGstin.length !== 15) {
      toast({
        title: "Incomplete GSTIN",
        description: "Please enter a complete 15-character GSTIN before verifying.",
        variant: "destructive",
      });
      return;
    }

    // Client-side checksum check before API call
    if (!liveAnalysis.checksumValid) {
      toast({
        title: "Invalid GSTIN Checksum",
        description: liveAnalysis.message || "The GSTIN checksum digit does not match.",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    try {
      const resp = await verifyGSTINApi(cleanGstin);

      if (!resp.success || !resp.data) {
        toast({
          title: "GSTIN Verification",
          description: resp.error || "Could not verify GSTIN. You can still enter details manually.",
          variant: "destructive",
        });
        return;
      }

      setVerifiedResult(resp.data);
      setVerificationSource(resp.source);
      setVerificationMessage(resp.message || "GSTIN verified successfully.");
      setShowPreviewModal(true);

      if (resp.status === "Active") {
        toast({
          title: "GSTIN Verified",
          description: resp.data.legalName
            ? `${resp.data.legalName} is Active on GST records.`
            : "GSTIN is Active on GST records.",
        });
      } else if (resp.status === "Cancelled" || resp.status === "Suspended") {
        toast({
          title: `GSTIN ${resp.status}`,
          description: `This business GSTIN status is marked as ${resp.status}.`,
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({
        title: "Verification Error",
        description: err.message || "Failed to reach verification service. Please enter details manually.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleApplyPreview = () => {
    if (!verifiedResult) return;
    onApplyVerification(verifiedResult);
    setShowPreviewModal(false);
    toast({
      title: "Company Details Auto-Filled",
      description: verifiedResult.legalName
        ? `Applied ${verifiedResult.legalName}. All fields remain fully editable.`
        : "GST details applied. Please complete legal name and address manually.",
    });
  };

  const getStatusBadge = (status: GSTRegistrationStatus) => {
    switch (status) {
      case "Active":
        return (
          <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white gap-1 px-2.5 py-0.5 text-xs font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" /> Active
          </Badge>
        );
      case "Cancelled":
        return (
          <Badge variant="destructive" className="gap-1 px-2.5 py-0.5 text-xs font-medium">
            <AlertTriangle className="w-3.5 h-3.5" /> Cancelled
          </Badge>
        );
      case "Suspended":
        return (
          <Badge className="bg-amber-500 hover:bg-amber-500 text-white gap-1 px-2.5 py-0.5 text-xs font-medium">
            <Clock className="w-3.5 h-3.5" /> Suspended
          </Badge>
        );
      case "Invalid":
        return (
          <Badge variant="destructive" className="gap-1 px-2.5 py-0.5 text-xs font-medium">
            <XCircle className="w-3.5 h-3.5" /> Invalid
          </Badge>
        );
      case "Unable to Verify":
        return (
          <Badge variant="secondary" className="bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200 gap-1 px-2.5 py-0.5 text-xs font-medium border border-amber-300 dark:border-amber-800">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Unable to Verify
          </Badge>
        );
      case "Unverified (Format Valid)":
        return (
          <Badge variant="secondary" className="bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300 gap-1 px-2.5 py-0.5 text-xs font-medium border border-sky-300 dark:border-sky-800">
            <ShieldCheck className="w-3.5 h-3.5 text-sky-600" /> Format Valid (Unverified)
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="gap-1 px-2.5 py-0.5 text-xs font-medium">
            <Info className="w-3.5 h-3.5" /> {status}
          </Badge>
        );
    }
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          <span>
            {label} {required && <span className="text-destructive">*</span>}
          </span>
          {verifiedResult && getStatusBadge(verifiedResult.status)}
        </Label>
        {verifiedResult && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowPreviewModal(true)}
            className="h-6 px-2 text-[11px] text-primary hover:text-primary font-medium"
          >
            <Sparkles className="w-3 h-3 mr-1" /> View GST Details
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            value={value}
            onChange={handleInputChange}
            placeholder={placeholder}
            maxLength={15}
            disabled={disabled || isVerifying}
            className="font-mono uppercase text-sm tracking-wide pr-8"
          />
          {liveAnalysis.isComplete && liveAnalysis.checksumValid && (
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-600" title="Checksum valid">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          )}
          {liveAnalysis.isComplete && !liveAnalysis.checksumValid && (
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-rose-500" title="Checksum invalid">
              <AlertTriangle className="w-4 h-4" />
            </div>
          )}
        </div>

        <Button
          type="button"
          onClick={handleVerify}
          disabled={disabled || isVerifying || cleanGstin.length !== 15 || !liveAnalysis.checksumValid}
          className="gap-1.5 shrink-0 bg-blue-600 hover:bg-blue-700 text-white font-medium"
        >
          {isVerifying ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
            </>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4" /> Verify GSTIN
            </>
          )}
        </Button>
      </div>

      {/* Helper & Checksum Feedback Strip */}
      <div className="flex items-center justify-between text-[11px] px-0.5">
        <span
          className={
            !liveAnalysis.hasInput
              ? "text-muted-foreground"
              : !liveAnalysis.isComplete
              ? "text-muted-foreground"
              : liveAnalysis.checksumValid
              ? "text-emerald-600 font-medium flex items-center gap-1"
              : "text-rose-500 font-medium flex items-center gap-1"
          }
        >
          {liveAnalysis.checksumValid && liveAnalysis.isComplete && <CheckCircle2 className="w-3 h-3" />}
          {!liveAnalysis.checksumValid && liveAnalysis.isComplete && <AlertTriangle className="w-3 h-3" />}
          {liveAnalysis.message || "15-character official GSTIN"}
        </span>

        {liveAnalysis.extracted?.state && (
          <span className="text-muted-foreground">
            {liveAnalysis.extracted.state.name} ({liveAnalysis.extracted.stateCode})
          </span>
        )}
      </div>

      {/* Confirmation Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-50 dark:bg-blue-950/40 rounded-lg text-blue-600">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold">GSTIN Verification & Auto-Fill</DialogTitle>
                  <DialogDescription className="text-xs">
                    Official registration records retrieved for <span className="font-mono font-semibold">{cleanGstin}</span>
                    {verificationSource === "api" && (
                      <span className="ml-1.5 text-emerald-600 font-medium inline-flex items-center gap-0.5">
                        • <ShieldCheck className="w-3 h-3" /> Verified via GSTN Registry (GSTINAPI.in)
                      </span>
                    )}
                    {verificationSource === "cache" && (
                      <span className="ml-1.5 text-blue-600 font-medium inline-flex items-center gap-0.5">
                        • <CheckCircle2 className="w-3 h-3" /> Verified Cache
                      </span>
                    )}
                    {verificationSource === "database" && (
                      <span className="ml-1.5 text-emerald-600 font-medium inline-flex items-center gap-0.5">
                        • <Database className="w-3 h-3" /> AccouPro Verified Registry
                      </span>
                    )}
                  </DialogDescription>
                </div>
              </div>
              {verifiedResult && getStatusBadge(verifiedResult.status)}
            </div>
          </DialogHeader>

          {verifiedResult && (
            <div className="space-y-4 py-2">
              {/* Warnings for Cancelled / Suspended */}
              {verifiedResult.status === "Cancelled" && (
                <div className="p-3 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 rounded-lg text-xs text-rose-800 dark:text-rose-300 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-rose-600" />
                  <div>
                    <strong className="font-semibold">Notice: Taxpayer Registration Cancelled.</strong> This GSTIN is
                    marked as Cancelled in official records. Please verify before proceeding.
                  </div>
                </div>
              )}

              {verifiedResult.status === "Suspended" && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                  <Clock className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                  <div>
                    <strong className="font-semibold">Notice: GST Registration Suspended.</strong> Compliance or
                    verification is temporarily suspended by tax authorities.
                  </div>
                </div>
              )}

              {/* Informational banner when offline format only */}
              {verifiedResult.isOfflineExtraction && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                  <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                  <div>
                    <strong className="font-semibold">Taxpayer Registry Record Not Found.</strong> The GSTIN format and
                    checksum are valid for {verifiedResult.state}, but legal business name and address were not returned by
                    the external GSP. Please enter company details manually.
                  </div>
                </div>
              )}

              {/* Verified Details Card */}
              <div className="border border-border rounded-lg bg-card p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Legal Business Name</span>
                    {verifiedResult.legalName ? (
                      <strong className="text-sm font-semibold text-foreground">
                        {verifiedResult.legalName}
                      </strong>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400 font-normal italic text-xs">
                        Not available — enter manually
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Trade / Brand Name</span>
                    {verifiedResult.tradeName || verifiedResult.legalName ? (
                      <strong className="text-sm font-semibold text-foreground">
                        {verifiedResult.tradeName || verifiedResult.legalName}
                      </strong>
                    ) : (
                      <span className="text-muted-foreground italic text-xs">Same as Legal Name</span>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Permanent Account Number (PAN)</span>
                    <span className="font-mono font-semibold text-foreground">{verifiedResult.pan || "-"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Constitution of Business</span>
                    <span className="font-medium text-foreground">{verifiedResult.constitution || "-"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Taxpayer Type</span>
                    <span className="font-medium text-foreground">{verifiedResult.taxpayerType || "Regular"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Registration Date</span>
                    <span className="font-medium text-foreground">
                      {verifiedResult.registrationDate || <span className="text-muted-foreground italic">Not provided in registry</span>}
                    </span>
                  </div>
                  {verifiedResult.cancellationDate && (
                    <div>
                      <span className="text-rose-500 block text-[11px] font-semibold">Cancellation Date</span>
                      <span className="font-semibold text-rose-600">{verifiedResult.cancellationDate}</span>
                    </div>
                  )}
                  {verifiedResult.jurisdiction && (
                    <div>
                      <span className="text-muted-foreground block text-[11px]">Jurisdiction</span>
                      <span className="font-medium text-foreground">{verifiedResult.jurisdiction}</span>
                    </div>
                  )}
                  {verifiedResult.einvoiceStatus && (
                    <div>
                      <span className="text-muted-foreground block text-[11px]">E-Invoicing</span>
                      <span className="font-medium text-foreground">{verifiedResult.einvoiceStatus === "Yes" ? "Applicable" : "Not Applicable"}</span>
                    </div>
                  )}
                </div>

                <div className="border-t border-border pt-3">
                  <span className="text-muted-foreground block text-[11px] mb-1">Principal Place of Business</span>
                  <div className="flex items-start gap-2 text-xs">
                    <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      {verifiedResult.address ? (
                        <>
                          <p className="text-foreground font-medium">{verifiedResult.address}</p>
                          <p className="text-muted-foreground">
                            {[verifiedResult.city, verifiedResult.state, verifiedResult.pincode].filter(Boolean).join(", ")}
                          </p>
                        </>
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400 italic text-xs">
                          Complete address not returned by registry — please enter street address below
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {verifiedResult.natureOfBusiness && verifiedResult.natureOfBusiness.length > 0 && (
                  <div className="border-t border-border pt-3">
                    <span className="text-muted-foreground block text-[11px] mb-1">Nature of Business Activities</span>
                    <div className="flex flex-wrap gap-1.5">
                      {verifiedResult.natureOfBusiness.map((item, idx) => (
                        <Badge key={idx} variant="outline" className="text-[11px]">
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* In Edit Mode or existing form values: Show diff comparison */}
              {isEditMode && currentFormValues && (verifiedResult.legalName || verifiedResult.address) && (
                <div className="border border-border/80 rounded-lg p-3 bg-muted/30">
                  <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-primary" /> Proposed Form Updates
                  </h4>
                  <div className="space-y-1.5 text-xs">
                    {verifiedResult.legalName && currentFormValues.name && currentFormValues.name !== (verifiedResult.tradeName || verifiedResult.legalName) && (
                      <div className="flex items-center justify-between py-1 border-b border-border/40">
                        <span className="text-muted-foreground">Company Name:</span>
                        <div className="flex items-center gap-1.5">
                          <span className="line-through text-muted-foreground">{currentFormValues.name}</span>
                          <ArrowRight className="w-3 h-3 text-primary" />
                          <strong className="text-foreground">{verifiedResult.tradeName || verifiedResult.legalName}</strong>
                        </div>
                      </div>
                    )}
                    {verifiedResult.legalName && currentFormValues.legalName && currentFormValues.legalName !== verifiedResult.legalName && (
                      <div className="flex items-center justify-between py-1 border-b border-border/40">
                        <span className="text-muted-foreground">Legal Name:</span>
                        <div className="flex items-center gap-1.5">
                          <span className="line-through text-muted-foreground">{currentFormValues.legalName}</span>
                          <ArrowRight className="w-3 h-3 text-primary" />
                          <strong className="text-foreground">{verifiedResult.legalName}</strong>
                        </div>
                      </div>
                    )}
                    {verifiedResult.address && currentFormValues.address && currentFormValues.address !== verifiedResult.address && (
                      <div className="flex items-center justify-between py-1 border-b border-border/40">
                        <span className="text-muted-foreground">Address:</span>
                        <div className="flex items-center gap-1.5 max-w-[65%] text-right">
                          <span className="truncate line-through text-muted-foreground">{currentFormValues.address}</span>
                          <ArrowRight className="w-3 h-3 text-primary shrink-0" />
                          <strong className="truncate text-foreground">{verifiedResult.address}</strong>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setShowPreviewModal(false)}>
              Keep Current / Dismiss
            </Button>
            <Button
              type="button"
              onClick={handleApplyPreview}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <CheckCircle2 className="w-4 h-4" /> Apply & Auto-Fill Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
